/**
 * Emerald Books — HR module (Employees, Policies, Org Chart).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Search, Users, UserPlus, X, Pencil, CheckCircle2,
  FileText, Send, ShieldCheck, LogOut, Building2, Globe, MapPin,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, unwrapList } from './api';

const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
  { code: 'IN', name: 'India', currency: 'INR' },
  { code: 'MY', name: 'Malaysia', currency: 'MYR' },
  { code: 'ID', name: 'Indonesia', currency: 'IDR' },
  { code: 'QA', name: 'Qatar', currency: 'QAR' },
  { code: 'KH', name: 'Cambodia', currency: 'KHR' },
  { code: 'KZ', name: 'Kazakhstan', currency: 'KZT' },
];
const EMP_TYPES = [
  { v: 'permanent', label: 'Permanent' },
  { v: 'fixed_term', label: 'Fixed-term' },
  { v: 'contract', label: 'Contract' },
  { v: 'intern', label: 'Intern' },
];
const STATUS_CLS = {
  active: 'bg-emerald-50 text-emerald-700',
  on_leave: 'bg-amber-50 text-amber-700',
  exited: 'bg-slate-100 text-slate-500',
};

/* ========================================================================= */
/* Employees                                                                   */
/* ========================================================================= */
export const HREmployeesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exitTarget, setExitTarget] = useState(null);
  const [form, setForm] = useState(_newEmp());
  const [exitForm, setExitForm] = useState({ exit_date: '', exit_reason: 'resignation', exit_notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const r = await booksClient.get('/hr/employees', { params });
      setItems(unwrapList(r));
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const openNew = () => { setEditing(null); setForm(_newEmp()); setShowForm(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ..._newEmp(), ...e }); setShowForm(true); };

  const submit = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...form, salary_monthly: Number(form.salary_monthly || 0) };
      if (editing) {
        await booksClient.patch(`/hr/employees/${editing.employee_id}`, payload);
        toast.success('Employee updated');
      } else {
        await booksClient.post('/hr/employees', payload);
        toast.success('Employee onboarded — task created');
      }
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitExit = async (ev) => {
    ev.preventDefault();
    try {
      await booksClient.post(`/hr/employees/${exitTarget.employee_id}/exit`, exitForm);
      toast.success('Exit recorded');
      setExitTarget(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Employees</h1>
          <p className="text-sm text-slate-500">Full employee lifecycle · onboarding · exits</p>
        </div>
        <Button data-testid="hr-emp-new-btn" onClick={openNew} className="bg-[#0f5c3d] hover:bg-[#0d4d33]">
          <UserPlus className="w-4 h-4 mr-1" /> Onboard
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Search name / code / email…" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="pl-9" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded text-sm bg-white">
          <option value="active">Active</option>
          <option value="on_leave">On leave</option>
          <option value="exited">Exited</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="hr-emp-list">
          {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No employees</div>}
          {items.map((e) => (
            <div key={e.employee_id} className="bg-white rounded-lg border p-4 hover:shadow-sm transition">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-mono text-slate-400">{e.employee_code}</div>
                  <div className="font-semibold text-slate-800 truncate">
                    {e.first_name} {e.last_name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{e.position || '—'}{e.department ? ` · ${e.department}` : ''}</div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLS[e.status] || 'bg-slate-100'}`}>
                  {(e.status || 'active').replace('_', ' ')}
                </span>
              </div>
              <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                {e.work_email && <div className="truncate">{e.work_email}</div>}
                <div className="flex items-center gap-2">
                  <Globe className="w-3 h-3" /> {e.country_code} · {e.currency}
                </div>
                {e.join_date && <div>Joined {e.join_date}</div>}
              </div>
              <div className="mt-3 flex items-center gap-1.5">
                <button onClick={() => openEdit(e)} data-testid={`hr-emp-edit-${e.employee_id}`}
                  className="text-xs px-2 py-1 rounded border hover:bg-slate-50 inline-flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                {e.status !== 'exited' && (
                  <button onClick={() => { setExitTarget(e); setExitForm({ exit_date: new Date().toISOString().slice(0, 10), exit_reason: 'resignation', exit_notes: '' }); }}
                    className="text-xs px-2 py-1 rounded border hover:bg-rose-50 text-rose-600 inline-flex items-center gap-1">
                    <LogOut className="w-3 h-3" /> Exit
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <EmployeeFormModal editing={editing} form={form} setForm={setForm} onClose={() => setShowForm(false)} onSubmit={submit} />}
      {exitTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={submitExit} className="bg-white rounded-lg p-5 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-800">Exit {exitTarget.first_name} {exitTarget.last_name}</div>
              <button type="button" onClick={() => setExitTarget(null)}><X className="w-4 h-4" /></button>
            </div>
            <Input type="date" value={exitForm.exit_date} onChange={(e) => setExitForm({ ...exitForm, exit_date: e.target.value })} required />
            <select value={exitForm.exit_reason} onChange={(e) => setExitForm({ ...exitForm, exit_reason: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm">
              <option value="resignation">Resignation</option>
              <option value="termination">Termination</option>
              <option value="retirement">Retirement</option>
              <option value="end_of_contract">End of contract</option>
              <option value="other">Other</option>
            </select>
            <textarea rows={3} className="w-full px-3 py-2 border rounded text-sm"
              placeholder="Exit notes"
              value={exitForm.exit_notes} onChange={(e) => setExitForm({ ...exitForm, exit_notes: e.target.value })} />
            <Button type="submit" className="w-full bg-rose-600 hover:bg-rose-700">Record exit</Button>
          </form>
        </div>
      )}
    </div>
  );
};

function _newEmp() {
  return {
    employee_code: '', first_name: '', last_name: '',
    personal_email: '', work_email: '', phone: '',
    country_code: 'GB', department: '', position: '',
    manager_id: '', join_date: new Date().toISOString().slice(0, 10),
    employment_type: 'permanent', salary_monthly: 0, currency: 'GBP',
    tax_id: '', bank_account: '', notes: '',
  };
}

const EmployeeFormModal = ({ editing, form, setForm, onClose, onSubmit }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
    <form onSubmit={onSubmit} className="bg-white rounded-lg p-5 w-full max-w-2xl space-y-3 my-8">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-slate-800">{editing ? 'Edit' : 'Onboard'} employee</div>
        <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Employee code *" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} required disabled={!!editing} />
        <Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} required />
        <Input placeholder="First name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
        <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        <Input placeholder="Work email" value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} />
        <Input placeholder="Personal email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} />
        <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <select value={form.country_code} onChange={(e) => {
          const c = COUNTRIES.find((x) => x.code === e.target.value);
          setForm({ ...form, country_code: e.target.value, currency: c?.currency || form.currency });
        }} className="px-3 py-2 border rounded text-sm">
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <Input placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
        <Input placeholder="Position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
        <select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
          className="px-3 py-2 border rounded text-sm">
          {EMP_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        <Input type="number" step="0.01" placeholder="Salary (monthly)" value={form.salary_monthly} onChange={(e) => setForm({ ...form, salary_monthly: e.target.value })} />
        <Input placeholder="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
        <Input placeholder="Tax ID (NINO / PAN / IC / IIN)" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
        <Input placeholder="Bank account" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} />
      </div>
      <textarea rows={2} className="w-full px-3 py-2 border rounded text-sm" placeholder="Notes"
        value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      <Button type="submit" data-testid="hr-emp-submit-btn" className="w-full bg-[#0f5c3d] hover:bg-[#0d4d33]">
        {editing ? 'Save changes' : 'Onboard employee'}
      </Button>
    </form>
  </div>
);

/* ========================================================================= */
/* Policies                                                                    */
/* ========================================================================= */
const CATEGORIES = [
  'code_of_conduct', 'data_privacy', 'ip', 'ai_usage', 'anti_harassment',
  'leave', 'expense', 'security', 'travel', 'local_appendix', 'other',
];

export const HRPoliciesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '', title: '', category: 'code_of_conduct', country_code: '',
    version: '1.0', effective_from: new Date().toISOString().slice(0, 10),
    body: '', requires_ack: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await booksClient.get('/hr/policies');
      setItems(unwrapList(r));
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.country_code) delete payload.country_code;
      await booksClient.post('/hr/policies', payload);
      toast.success('Policy drafted');
      setShowForm(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const publish = async (id) => {
    if (!window.confirm('Publish this policy? All active employees will be assigned a task to acknowledge.')) return;
    try {
      await booksClient.post(`/hr/policies/${id}/publish`);
      toast.success('Policy published — acknowledgement tasks created');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Policies</h1>
          <p className="text-sm text-slate-500">Publish policies & track acknowledgements</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="hr-policy-new-btn" className="bg-[#0f5c3d] hover:bg-[#0d4d33]">
          <Plus className="w-4 h-4 mr-1" /> New policy
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input placeholder="Code (e.g. POSH-IN)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
            <Input placeholder="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} required />
            <Input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="sm:col-span-2" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="px-3 py-2 border rounded text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
            <select value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })}
              className="px-3 py-2 border rounded text-sm">
              <option value="">Global (all countries)</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.requires_ack} onChange={(e) => setForm({ ...form, requires_ack: e.target.checked })} />
              Requires acknowledgement
            </label>
          </div>
          <textarea rows={6} className="w-full px-3 py-2 border rounded text-sm" placeholder="Policy body (markdown supported)"
            value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <Button type="submit" className="w-full bg-[#0f5c3d] hover:bg-[#0d4d33]">Save draft</Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="hr-policy-list">
          {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No policies</div>}
          {items.map((p) => (
            <div key={p.policy_id} className="bg-white rounded-lg border p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-mono text-slate-400">{p.code} · v{p.version}</div>
                  <div className="font-semibold text-slate-800">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.category.replace(/_/g, ' ')} · {p.country_code || 'global'} · from {p.effective_from}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${p.published ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {p.published ? 'Published' : 'Draft'}
                </span>
              </div>
              {!p.published && (
                <Button size="sm" onClick={() => publish(p.policy_id)} data-testid={`hr-policy-publish-${p.policy_id}`}
                  className="mt-3 bg-[#0f5c3d] hover:bg-[#0d4d33]">
                  <Send className="w-3.5 h-3.5 mr-1" /> Publish
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================================================================= */
/* Org Chart                                                                   */
/* ========================================================================= */
export const HROrgChartPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await booksClient.get('/hr/org-chart');
        setData(r.data);
      } catch (e) { toast.error(errMsg(e)); }
      setLoading(false);
    })();
  }, []);

  const renderNode = (n, depth = 0) => (
    <div key={n.employee_id} className="pl-4 border-l border-slate-200 mt-2">
      <div className="bg-white rounded border p-2 inline-block">
        <div className="text-xs font-mono text-slate-400">{n.employee_code}</div>
        <div className="font-semibold text-slate-800 text-sm">{n.first_name} {n.last_name}</div>
        <div className="text-xs text-slate-500">{n.position || '—'} · {n.country_code}</div>
      </div>
      {(n.reports || []).map((c) => renderNode(c, depth + 1))}
    </div>
  );

  if (loading) return <Loader2 className="animate-spin mx-auto text-slate-400" />;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-[#0b1a2e]">Org Chart</h1>
        <p className="text-sm text-slate-500">Reporting hierarchy · {data?.count || 0} people</p>
      </div>
      <div className="bg-slate-50 p-4 rounded-lg border overflow-auto">
        {(data?.tree || []).map((r) => renderNode(r))}
      </div>
    </div>
  );
};
